import React, { useEffect, useState } from 'react';
import { adminAPI } from '../api';

/**
 * Admin → Platform → Activity.
 *
 * One quiet view that lists two things side-by-side:
 *   • System events — the operational journal (backups, reconciliations, etc.)
 *   • Audit log — meaningful admin actions (renewals, user state changes, ...)
 *
 * Both lists are newest-first. The audit log has a tiny substring filter
 * so you can paste a board id, email, or action name. No charts, no graphs,
 * no metrics. The bar for adding a column to either list is: "I'd want to
 * see this when scanning a week of activity."
 */

const SEVERITY_DOT = {
  info: '#a9b39f',       // muted sage — informational
  warning: '#b88a4a',    // muted amber
  attention: '#a04141',  // soft brick red
};

const _fmtTime = (iso) => {
  if (!iso) return '';
  try {
    const d = new Date(iso);
    if (Number.isNaN(d.getTime())) return iso;
    const now = new Date();
    const sameDay = d.toDateString() === now.toDateString();
    if (sameDay) {
      return d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    }
    return d.toLocaleDateString([], { day: '2-digit', month: 'short' }) +
      ' · ' + d.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

const SectionHeading = ({ label, hint }) => (
  <div style={{ marginBottom: 18 }}>
    <p style={{
      margin: 0,
      fontSize: 10,
      letterSpacing: '0.22em',
      textTransform: 'uppercase',
      color: 'var(--kahla-text-light)',
      fontWeight: 500,
    }}>{label}</p>
    {hint && (
      <p style={{
        margin: '6px 0 0',
        fontFamily: 'Cormorant Garamond, Playfair Display, serif',
        fontStyle: 'italic',
        fontSize: 13,
        color: 'var(--kahla-text-light)',
      }}>{hint}</p>
    )}
  </div>
);

const EmptyLine = ({ phrase }) => (
  <p style={{
    fontFamily: 'Cormorant Garamond, Playfair Display, serif',
    fontStyle: 'italic',
    fontSize: 13,
    color: 'var(--kahla-text-light)',
    margin: '24px 0',
  }}>{phrase}</p>
);

const SystemEventRow = ({ event }) => (
  <li
    data-testid={`activity-event-${event.type}`}
    style={{
      display: 'grid',
      gridTemplateColumns: 'auto 1fr auto',
      gap: 14,
      alignItems: 'baseline',
      padding: '11px 0',
      borderBottom: '1px solid var(--kahla-divider-soft, #f0ebe1)',
    }}
  >
    <span aria-hidden style={{
      display: 'inline-block',
      width: 7,
      height: 7,
      borderRadius: '50%',
      background: SEVERITY_DOT[event.severity] || SEVERITY_DOT.info,
    }} />
    <div>
      <p style={{
        margin: 0,
        fontSize: 13,
        color: 'var(--kahla-charcoal, #2a2a2a)',
      }}>{event.short_message}</p>
      <p style={{
        margin: '3px 0 0',
        fontSize: 10,
        letterSpacing: '0.14em',
        textTransform: 'uppercase',
        color: 'var(--kahla-text-light)',
      }}>{event.type}</p>
    </div>
    <span style={{
      fontFamily: 'Cormorant Garamond, Playfair Display, serif',
      fontStyle: 'italic',
      fontSize: 12,
      color: 'var(--kahla-text-light)',
      whiteSpace: 'nowrap',
    }}>{_fmtTime(event.created_at)}</span>
  </li>
);

const AuditRow = ({ entry }) => (
  <li
    data-testid={`activity-audit-${entry.action}`}
    style={{
      display: 'grid',
      gridTemplateColumns: '1fr auto',
      gap: 14,
      alignItems: 'baseline',
      padding: '11px 0',
      borderBottom: '1px solid var(--kahla-divider-soft, #f0ebe1)',
    }}
  >
    <div>
      <p style={{
        margin: 0,
        fontSize: 13,
        color: 'var(--kahla-charcoal, #2a2a2a)',
      }}>
        <strong style={{ fontWeight: 500 }}>{entry.admin_user}</strong>
        {' '}
        <span style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          color: 'var(--kahla-text-light)',
        }}>→</span>
        {' '}
        {entry.action}
        {entry.target && (
          <span style={{ color: 'var(--kahla-text-light)' }}> · {entry.target}</span>
        )}
      </p>
    </div>
    <span style={{
      fontFamily: 'Cormorant Garamond, Playfair Display, serif',
      fontStyle: 'italic',
      fontSize: 12,
      color: 'var(--kahla-text-light)',
      whiteSpace: 'nowrap',
    }}>{_fmtTime(entry.timestamp)}</span>
  </li>
);

const AdminActivityTab = () => {
  const [events, setEvents] = useState([]);
  const [audit, setAudit] = useState([]);
  const [q, setQ] = useState('');
  const [loading, setLoading] = useState(true);
  const [reconciling, setReconciling] = useState(false);
  const [lastReconcileVerdict, setLastReconcileVerdict] = useState(null);

  const loadEvents = () => adminAPI.listSystemEvents().then((r) => setEvents(r.data.events || []));

  // Audit re-fetches when `q` changes (debounced to avoid spamming).
  useEffect(() => {
    let alive = true;
    const t = setTimeout(() => {
      adminAPI.listAuditLog(q).then((r) => { if (alive) setAudit(r.data.entries || []); });
    }, q ? 220 : 0);
    return () => { alive = false; clearTimeout(t); };
  }, [q]);

  // Events load once on mount — the journal is append-only and we don't
  // need a live filter for it.
  useEffect(() => {
    let alive = true;
    Promise.all([loadEvents()])
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, []);

  // Manual reconcile — kicks off a fresh scan and refreshes both lists
  // (the new system_event and the new audit row appear naturally on the
  // next fetch). Verdict phrase fades in next to the button for ~6s.
  const handleReconcile = async () => {
    if (reconciling) return;
    setReconciling(true);
    setLastReconcileVerdict(null);
    try {
      const r = await adminAPI.triggerReconciliation();
      const phrase = (r.data && r.data.phrase) || 'Reconciliation complete.';
      setLastReconcileVerdict(phrase);
      await loadEvents();
      const audit_r = await adminAPI.listAuditLog(q);
      setAudit(audit_r.data.entries || []);
      window.setTimeout(() => setLastReconcileVerdict(null), 6000);
    } catch {
      setLastReconcileVerdict('Reconciliation could not run right now.');
    } finally {
      setReconciling(false);
    }
  };

  return (
    <div data-testid="admin-activity-tab" style={{ maxWidth: 980 }}>
      <div style={{
        display: 'flex',
        alignItems: 'flex-start',
        justifyContent: 'space-between',
        gap: 24,
        marginBottom: 40,
      }}>
        <p style={{
          fontFamily: 'Cormorant Garamond, Playfair Display, serif',
          fontStyle: 'italic',
          fontSize: 15,
          color: 'var(--kahla-text-light)',
          margin: 0,
          maxWidth: 560,
        }}>
          A quiet operational journal. System events on the left, meaningful
          admin actions on the right. Newest first.
        </p>
        <div style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'flex-end',
          gap: 6,
          flexShrink: 0,
        }}>
          <button
            type="button"
            onClick={handleReconcile}
            disabled={reconciling}
            data-testid="trigger-reconcile-btn"
            style={{
              fontFamily: 'Inter, sans-serif',
              fontSize: 11,
              letterSpacing: '0.18em',
              textTransform: 'uppercase',
              color: 'var(--kahla-charcoal, #2a2a2a)',
              background: 'transparent',
              border: '1px solid var(--kahla-divider, #e8e3da)',
              borderRadius: 2,
              padding: '9px 18px',
              cursor: reconciling ? 'default' : 'pointer',
              opacity: reconciling ? 0.55 : 1,
              transition: 'background 160ms ease, border-color 160ms ease',
            }}
            onMouseEnter={(e) => {
              if (!reconciling) e.currentTarget.style.background = 'var(--kahla-cream-light)';
            }}
            onMouseLeave={(e) => {
              e.currentTarget.style.background = 'transparent';
            }}
          >
            {reconciling ? 'Reconciling…' : 'Re-check storage'}
          </button>
          {lastReconcileVerdict && (
            <p
              data-testid="trigger-reconcile-verdict"
              style={{
                margin: 0,
                fontFamily: 'Cormorant Garamond, Playfair Display, serif',
                fontStyle: 'italic',
                fontSize: 12,
                color: 'var(--kahla-text-light)',
                maxWidth: 260,
                textAlign: 'right',
                lineHeight: 1.5,
              }}
            >
              {lastReconcileVerdict}
            </p>
          )}
        </div>
      </div>

      <div style={{
        display: 'grid',
        gridTemplateColumns: 'minmax(280px, 1fr) minmax(280px, 1fr)',
        gap: 48,
        alignItems: 'start',
      }}>
        {/* System Events */}
        <section data-testid="activity-events-column">
          <SectionHeading
            label="System Events"
            hint="Backups, reconciliation, delivery — the things the platform does on its own."
          />
          {loading ? null : events.length === 0 ? (
            <EmptyLine phrase="No events recorded yet." />
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {events.map((e, i) => <SystemEventRow key={i} event={e} />)}
            </ul>
          )}
        </section>

        {/* Audit Log */}
        <section data-testid="activity-audit-column">
          <SectionHeading
            label="Admin Audit"
            hint="Meaningful admin actions: renewals, user state, demo, reconciliation triggers."
          />
          <input
            data-testid="activity-audit-search"
            type="text"
            placeholder="Filter by admin, action or target"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            style={{
              width: '100%',
              padding: '9px 12px',
              marginBottom: 14,
              border: '1px solid var(--kahla-divider, #e8e3da)',
              borderRadius: 2,
              background: 'transparent',
              fontFamily: 'inherit',
              fontSize: 13,
              color: 'var(--kahla-charcoal, #2a2a2a)',
              outline: 'none',
            }}
          />
          {audit.length === 0 ? (
            <EmptyLine phrase={q ? 'No entries match this filter.' : 'No admin actions recorded yet.'} />
          ) : (
            <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
              {audit.map((a, i) => <AuditRow key={i} entry={a} />)}
            </ul>
          )}
        </section>
      </div>
    </div>
  );
};

export default AdminActivityTab;
